import { Component, createSignal, onMount, Show } from 'solid-js';
import { TbX, TbAlertCircle, TbAlertTriangle, TbCheck, TbInfoCircle } from 'solid-icons/tb';
import { newsState } from '../../services/newsService';
import { config } from '../../config/environment';

/**
 * Format timestamp as human-friendly relative time
 * Examples: "just now", "2 minutes ago", "1 hour ago", "3 days ago"
 */
const formatRelativeTime = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    const now = new Date();
    const secondsAgo = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (secondsAgo < 60) {
      return 'just now';
    }

    const minutesAgo = Math.floor(secondsAgo / 60);
    if (minutesAgo < 60) {
      return minutesAgo === 1 ? '1 minute ago' : `${minutesAgo} minutes ago`;
    }

    const hoursAgo = Math.floor(minutesAgo / 60);
    if (hoursAgo < 24) {
      return hoursAgo === 1 ? '1 hour ago' : `${hoursAgo} hours ago`;
    }

    const daysAgo = Math.floor(hoursAgo / 24);
    if (daysAgo < 7) {
      return daysAgo === 1 ? '1 day ago' : `${daysAgo} days ago`;
    }

    const weeksAgo = Math.floor(daysAgo / 7);
    if (weeksAgo < 4) {
      return weeksAgo === 1 ? '1 week ago' : `${weeksAgo} weeks ago`;
    }

    const monthsAgo = Math.floor(daysAgo / 30);
    if (monthsAgo < 12) {
      return monthsAgo === 1 ? '1 month ago' : `${monthsAgo} months ago`;
    }

    const yearsAgo = Math.floor(monthsAgo / 12);
    return yearsAgo === 1 ? '1 year ago' : `${yearsAgo} years ago`;
  } catch {
    return 'unknown time';
  }
};

interface News {
  id: string;
  title: string;
  content: string;
  type?: 'info' | 'warning' | 'success' | 'error';
  created_at?: string;
}

interface NewsModalProps {
  isOpen: boolean;
  onClose: () => void;
  news: News | null;
}

/**
 * News Modal Component
 *
 * Displays news/announcements in a modal after user login.
 * Features:
 * - Customizable news types (info, warning, success, error)
 * - Clean, readable layout
 * - Close button and backdrop click to dismiss
 * - Auto-style based on news type
 */
const NewsModal: Component<NewsModalProps> = (props) => {
  const handleClose = async () => {
    // Mark news as read when modal is closed
    if (props.news?.id) {
      await newsState.markNewsAsRead(props.news.id);
    }
    props.onClose();
  };

  const getTypeIcon = (type?: string) => {
    switch (type) {
      case 'warning':
        return <TbAlertTriangle class="w-6 h-6" />;
      case 'success':
        return <TbCheck class="w-6 h-6" />;
      case 'error':
        return <TbAlertCircle class="w-6 h-6" />;
      case 'info':
      default:
        return <TbInfoCircle class="w-6 h-6" />;
    }
  };

  const getTypeStyles = () => {
    const type = props.news?.type || 'info';
    const styles = {
      info: {
        headerBg: 'bg-orange-500 dark:bg-orange-600',
        icon: 'text-orange-200',
        contentBg: 'bg-orange-50 dark:bg-gray-800',
        text: 'text-gray-700 dark:text-gray-300',
        badge: 'bg-orange-100 dark:bg-orange-900/50 text-orange-700 dark:text-orange-300'
      },
      warning: {
        headerBg: 'bg-orange-500 dark:bg-orange-600',
        icon: 'text-orange-200',
        contentBg: 'bg-orange-50 dark:bg-gray-800',
        text: 'text-gray-700 dark:text-gray-300',
        badge: 'bg-orange-100 dark:bg-orange-900/50 text-orange-700 dark:text-orange-300'
      },
      success: {
        headerBg: 'bg-orange-500 dark:bg-orange-600',
        icon: 'text-orange-200',
        contentBg: 'bg-orange-50 dark:bg-gray-800',
        text: 'text-gray-700 dark:text-gray-300',
        badge: 'bg-orange-100 dark:bg-orange-900/50 text-orange-700 dark:text-orange-300'
      },
      error: {
        headerBg: 'bg-orange-500 dark:bg-orange-600',
        icon: 'text-orange-200',
        contentBg: 'bg-orange-50 dark:bg-gray-800',
        text: 'text-gray-700 dark:text-gray-300',
        badge: 'bg-orange-100 dark:bg-orange-900/50 text-orange-700 dark:text-orange-300'
      }
    };
    return styles[type];
  };

  return (
    <Show when={props.isOpen && props.news}>
      {/* Backdrop - covers entire scrollable page */}
      <div
        class="fixed inset-0 bg-black/80 backdrop-blur-sm z-40 transition-opacity"
        onClick={handleClose}
      />

      {/* Modal Container - Centered on screen */}
      <div class="fixed inset-0 z-50 flex items-center justify-center pointer-events-none">
        {/* Modal - Centered on screen */}
        <div class="pointer-events-auto max-w-xs xs:max-w-sm sm:max-w-md max-h-[75vh] xs:max-h-[80vh] overflow-y-auto bg-white dark:bg-slate-900 rounded-xl shadow-2xl border border-gray-200 dark:border-slate-700 animate-slideIn">
        {/* Header with colored background */}
        <div class={`px-4 xs:px-6 py-3 xs:py-4 ${getTypeStyles().headerBg} flex items-center justify-between`}>
          <div class="flex items-center gap-3">
            <div class={`${getTypeStyles().icon}`}>
              {getTypeIcon(props.news?.type)}
            </div>
            <div>
              <p class="text-xs font-semibold text-white uppercase opacity-90">
                {props.news?.type === 'warning' ? 'Warning' : props.news?.type === 'success' ? 'Success' : props.news?.type === 'error' ? 'Error' : 'Information'}
              </p>
              <h2 class="text-white font-bold text-base xs:text-lg">
                {config.dashboard.appName} Notification
              </h2>
            </div>
          </div>
          <button
            onClick={handleClose}
            class="text-white/80 hover:text-white text-2xl leading-none transition-colors flex-shrink-0"
            title="Close modal"
          >
            <TbX size={24} />
          </button>
        </div>

        {/* Content */}
        <div class={`p-4 xs:p-6 ${getTypeStyles().contentBg}`}>
          <p class={`text-sm leading-relaxed whitespace-pre-wrap ${getTypeStyles().text} mb-4`}>
            {props.news?.content}
          </p>

          {/* Timestamp */}
          <Show when={props.news?.created_at}>
            <p class="text-xs text-gray-500 dark:text-gray-400">
              Posted: {formatRelativeTime(props.news!.created_at || '')}
            </p>
          </Show>
        </div>

        {/* Footer */}
        <div class="px-4 xs:px-6 py-3 bg-gray-50 dark:bg-slate-800 border-t border-gray-200 dark:border-slate-700 flex gap-2 justify-end">
          <button
            onClick={handleClose}
            class="px-4 py-2 bg-orange-500 hover:bg-orange-600 dark:bg-orange-600 dark:hover:bg-orange-700 text-white font-medium rounded-lg transition-colors text-sm"
          >
            Dismiss
          </button>
        </div>
        </div>
      </div>
    </Show>
  );
};

export default NewsModal;
